/**
 * CardsJuridicos — o par de cards "Impugnações / Defesas" do dashboard
 * original: número grande, ícone rotacionado com badge, glow no hover e
 * detalhes expansíveis ao clicar.
 */

import {
  faChevronDown,
  faFileAlt,
  faGavel,
  faRobot,
  faShieldAlt,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { resumoDashboard } from '../../../api/dashboard'

function CardPeca({
  rotulo, valor, url, icone, glow, borda, iconeFundo, corNumero, corHoverTitulo,
  fundoDetalhe, bordaDetalhe, corDetalhe, corCirculo, decorativo,
}: {
  rotulo: string
  valor: number
  url: string
  icone: IconDefinition
  glow: string
  borda: string
  iconeFundo: string
  corNumero: string
  corHoverTitulo: string
  fundoDetalhe: string
  bordaDetalhe: string
  corDetalhe: string
  corCirculo: string
  decorativo: string
}) {
  const [expandido, setExpandido] = useState(false)

  return (
    <div className="group relative">
      <div className={`absolute inset-0 rounded-2xl opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-20 ${glow}`} />
      <button
        onClick={() => setExpandido((v) => !v)}
        className={`relative w-full overflow-hidden rounded-2xl border bg-white p-6 text-left shadow-lg transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl dark:bg-dark-card ${borda} ${
          expandido ? 'scale-[1.01] ring-2' : ''
        }`}
      >
        <div className={`absolute right-0 top-0 h-40 w-40 -translate-y-20 translate-x-10 rounded-full bg-gradient-to-br transition-transform duration-700 group-hover:scale-110 ${decorativo}`} />

        <div className="relative z-10 flex items-start gap-5">
          <div className="relative">
            <div className={`flex h-16 w-16 rotate-3 items-center justify-center rounded-2xl shadow-lg transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110 ${iconeFundo}`}>
              <FontAwesomeIcon icon={icone} className="text-2xl text-white" />
            </div>
            <div className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full border border-slate-100 bg-white shadow-sm dark:border-slate-700 dark:bg-dark-card">
              <FontAwesomeIcon icon={faFileAlt} className={`text-sm ${corNumero}`} />
            </div>
          </div>

          <div className="flex-1 pt-1">
            <h3 className={`mb-1 text-sm font-bold uppercase tracking-widest text-slate-500 transition-colors dark:text-slate-400 ${corHoverTitulo}`}>
              {rotulo}
            </h3>
            <div className="flex items-baseline gap-2">
              <span className={`text-5xl font-black tabular-nums tracking-tight ${corNumero}`}>
                {valor}
              </span>
              <span className="text-sm font-medium text-slate-400">geradas</span>
            </div>
          </div>

          <div className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 ${corCirculo}`}>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`${corNumero} transition-transform duration-300 ${expandido ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {expandido && (
          <div className={`relative z-10 mt-6 grid grid-cols-2 gap-4 border-t pt-6 ${bordaDetalhe}`}>
            <div className={`rounded-xl border p-3 ${fundoDetalhe} ${bordaDetalhe}`}>
              <p className={`mb-1 text-[10px] font-bold uppercase ${corDetalhe}`}>Processamento</p>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faRobot} className={corNumero} />
                <span className={`text-sm font-bold ${corNumero}`}>IA Premium</span>
              </div>
            </div>
            <a
              href={url}
              onClick={(e) => e.stopPropagation()}
              className={`flex items-center justify-center rounded-xl border p-3 text-sm font-bold transition-colors ${fundoDetalhe} ${bordaDetalhe} ${corNumero} hover:underline`}
            >
              Ver todas
            </a>
          </div>
        )}
      </button>
    </div>
  )
}

export function CardsJuridicos() {
  const { data: resumo } = useQuery({
    queryKey: ['dashboard-resumo'],
    queryFn: resumoDashboard,
  })
  if (!resumo) return null

  return (
    <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
      <CardPeca
        rotulo="Impugnações"
        valor={resumo.juridico.impugnacoes}
        url="/juridico/impugnacao"
        icone={faGavel}
        glow="bg-gradient-to-br from-rose-400 to-pink-600"
        borda="border-rose-200 ring-rose-400 dark:border-rose-900/50"
        iconeFundo="bg-gradient-to-br from-rose-500 to-pink-600 shadow-rose-500/30"
        corNumero="text-rose-600 dark:text-rose-400"
        corHoverTitulo="group-hover:text-rose-600 dark:group-hover:text-rose-400"
        fundoDetalhe="bg-rose-50 dark:bg-rose-900/20"
        bordaDetalhe="border-rose-100 dark:border-rose-800/30"
        corDetalhe="text-rose-400"
        corCirculo="bg-rose-50 group-hover:bg-rose-100 dark:bg-rose-900/30"
        decorativo="from-rose-100 to-transparent dark:from-rose-900/30"
      />
      <CardPeca
        rotulo="Defesas"
        valor={resumo.juridico.defesas}
        url="/juridico/defesa"
        icone={faShieldAlt}
        glow="bg-gradient-to-br from-teal-400 to-emerald-600"
        borda="border-teal-200 ring-teal-400 dark:border-teal-900/50"
        iconeFundo="bg-gradient-to-br from-teal-500 to-emerald-600 shadow-teal-500/30"
        corNumero="text-teal-600 dark:text-teal-400"
        corHoverTitulo="group-hover:text-teal-600 dark:group-hover:text-teal-400"
        fundoDetalhe="bg-teal-50 dark:bg-teal-900/20"
        bordaDetalhe="border-teal-100 dark:border-teal-800/30"
        corDetalhe="text-teal-400"
        corCirculo="bg-teal-50 group-hover:bg-teal-100 dark:bg-teal-900/30"
        decorativo="from-teal-100 to-transparent dark:from-teal-900/30"
      />
    </div>
  )
}
